// Copyright (c) 2025 Bytedance Ltd. and/or its affiliates
// SPDX-License-Identifier: MIT

import { StarFilledIcon, GitHubLogoIcon } from "@radix-ui/react-icons";
import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import { LogInIcon, UserPlusIcon, LogOutIcon, ArrowLeftIcon, XIcon } from "lucide-react";
import { useRouter } from "next/navigation";

import { NumberTicker } from "@/components/magicui/number-ticker";
import { Button } from "@/components/ui/button";
import { env } from "@/env";
import AuthLoginForm from "@/components/AuthLoginForm";
import AuthRegisterForm from "@/components/AuthRegisterForm";

export function SiteHeader() {
  // 登录状态和表单切换
  const [user, setUser] = useState<{ username: string; token: string } | null>(null);
  const [showLogin, setShowLogin] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // 设置模态框初始位置为屏幕中心
  useEffect(() => {
    if (showAuth) {
      setDragPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      });
      // 延迟显示动画
      setTimeout(() => setIsModalVisible(true), 10);
    } else {
      setIsModalVisible(false);
    }
  }, [showAuth]);

  // 组件挂载时从 localStorage 读取登录信息
  useEffect(() => {
    const stored = localStorage.getItem("bioagent_user");
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {}
    }
  }, []);

  // 拖拽相关事件处理
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!modalRef.current) return;
    
    const rect = modalRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    setDragPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 添加和移除全局鼠标事件监听器
  useEffect(() => {
    if (showAuth) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [showAuth, isDragging, dragOffset]);

  // 登录、注册、退出回调
  const handleLogin = (username: string, token: string) => {
    const userObj = { username, token };
    setUser(userObj);
    localStorage.setItem("bioagent_user", JSON.stringify(userObj));
    closeModal();
    router.push("/chat");
  };
  const handleRegister = (username: string, token: string) => {
    const userObj = { username, token };
    setUser(userObj);
    localStorage.setItem("bioagent_user", JSON.stringify(userObj));
    closeModal();
    router.push("/chat");
  };
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("bioagent_user");
    router.push("/");
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setTimeout(() => setShowAuth(false), 300);
  };

  return (
    <header className="bg-black text-white sticky top-0 left-0 z-40 flex h-15 w-full flex-col items-center">
      <div className="container flex h-15 items-center justify-between px-3">
        <div className="text-xl font-medium">
          <span className="mr-1 text-2xl">🦌</span>
          <a href="/" className="hover:underline text-white">BioAgent</a>
        </div>
        <div className="relative flex items-center gap-4">
          <div
            className="pointer-events-none absolute inset-0 z-0 h-full w-full rounded-full opacity-60 blur-2xl"
            style={{
              background: "linear-gradient(90deg, #ff80b5 0%, #9089fc 100%)",
              filter: "blur(32px)",
            }}
          />

          {/* 登录/注册/注销入口 */}
          {!user ? (
            <>
              <Button
                variant="outline"
                size="sm"
                className="group relative z-10 flex items-center text-black cursor-pointer bg-white hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl"
                onClick={() => setShowAuth((prev) => !prev)}
              >
                <LogInIcon className="size-4 mr-1 text-black" />
                登录/注册
              </Button>
              {showAuth && (
                <>
                  {/* 背景遮罩 */}
                  <div 
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300"
                    onClick={closeModal}
                  />
                  {/* 可拖动的模态框 */}
                  <div
                    ref={modalRef}
                    className={`fixed z-50 bg-white backdrop-blur-xl text-black rounded-2xl shadow-2xl border border-gray-200 min-w-[420px] max-w-[460px] overflow-hidden transition-all duration-300 ${
                      isModalVisible 
                        ? 'opacity-100 scale-100' 
                        : 'opacity-0 scale-95'
                    }`}
                    style={{
                      left: dragPosition.x,
                      top: dragPosition.y,
                      transform: 'translate(-50%, -50%)'
                    }}
                  >
                    {/* 拖拽标题栏 */}
                    <div 
                      className="flex items-center justify-between p-4 border-b border-gray-200 cursor-grab active:cursor-grabbing bg-gray-50"
                      onMouseDown={handleMouseDown}
                    >
                      <div className="flex items-center">
                        <button
                          className="flex items-center text-gray-600 hover:text-blue-600 text-sm mr-2 cursor-pointer transition-colors duration-200"
                          onClick={() => { window.location.href = "/"; setShowAuth(false); }}
                        >
                          <ArrowLeftIcon className="size-4 mr-1" /> 回到首页
                        </button>
                      </div>
                      <button
                        className="text-gray-500 hover:text-gray-700 cursor-pointer p-1 rounded-full hover:bg-gray-200 transition-all duration-200"
                        onClick={closeModal}
                      >
                        <XIcon className="size-4" />
                      </button>
                    </div>
                    
                    {/* 表单内容 */}
                    <div className="p-8">
                      {showLogin ? (
                        <>
                          <AuthLoginForm onLogin={handleLogin} />
                          <button 
                            className="mt-6 text-blue-400 hover:text-blue-300 text-sm cursor-pointer w-full text-center transition-colors duration-200 flex items-center justify-center" 
                            onClick={() => setShowLogin(false)}
                          >
                            <UserPlusIcon className="inline size-4 mr-1" />没有账号？去注册
                          </button>
                        </>
                      ) : (
                        <>
                          <AuthRegisterForm onRegister={handleRegister} />
                          <button 
                            className="mt-6 text-blue-400 hover:text-blue-300 text-sm w-full text-center transition-colors duration-200 flex items-center justify-center" 
                            onClick={() => setShowLogin(true)}
                          >
                            <LogInIcon className="inline size-4 mr-1" />已有账号？去登录
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-white">{user.username}</span>
              <Button
                variant="outline"
                size="sm"
                className="group relative z-10 flex items-center text-black cursor-pointer bg-white hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl"
                onClick={handleLogout}
              >
                <LogOutIcon className="size-4 mr-1 text-black cursor-pointer" /> 注销
              </Button>
            </div>
          )}
        </div>
      </div>
      <hr className="from-border/0 via-border/70 to-border/0 m-0 h-px w-full border-none bg-gradient-to-r" />
    </header>
  );
}

export async function StarCounter() {
  let stars = 1000; // Default value

  try {
    const response = await fetch(
      "https://api.github.com/repos/bytedance/deer-flow",
      {
        headers: env.GITHUB_OAUTH_TOKEN
          ? {
              Authorization: `Bearer ${env.GITHUB_OAUTH_TOKEN}`,
              "Content-Type": "application/json",
            }
          : {},
        next: {
          revalidate: 3600,
        },
      },
    );

    if (response.ok) {
      const data = await response.json();
      stars = data.stargazers_count ?? stars; // Update stars if API response is valid
    }
  } catch (error) {
    console.error("Error fetching GitHub stars:", error);
  }
  return (
    <>
      <StarFilledIcon className="size-4 transition-colors duration-300 group-hover:text-yellow-500" />
      {stars && (
        <NumberTicker className="font-mono tabular-nums" value={stars} />
      )}
    </>
  );
}
